import { useState } from "react";
import { Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { opsApi } from "../lib/api";

/** Nothing leaves the building unread: every email is shown as a draft first. */
export default function EmailDialog({ projectId, draft, onClose }) {
  const [to, setTo] = useState((draft.to || []).join(", "));
  const [subject, setSubject] = useState(draft.subject || "");
  const [body, setBody] = useState(draft.body || "");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const recipients = to.split(",").map((s) => s.trim()).filter(Boolean);
    if (!recipients.length) { toast.error("Add at least one recipient"); return; }
    setBusy(true);
    try {
      const res = await opsApi.sendEmail(projectId, { kind: draft.kind, to: recipients, subject, body });
      toast.success(`Sent to ${res.sent.join(", ")}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.detail || e?.response?.data?.detail || "The email could not be sent");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-email-dialog">
        <h3 className="text-base font-semibold text-slate-900">
          {draft.kind === "ready_accounting" ? "Ask Accounting to open the job" : "Request job opening"}
        </h3>
        <p className="mt-1 text-xs text-slate-500">Read it before it goes. Replies come back to you.</p>

        <label className="mt-4 block text-xs font-medium text-slate-500">To</label>
        <Input value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 h-9 text-sm"
               placeholder="accounting@absinc.com" data-testid="ops-email-to" />
        {!draft.to?.length && (
          <p className="mt-1 text-xs text-amber-600">
            No Controller account was found — type the address you want this to go to.
          </p>
        )}

        <label className="mt-3 block text-xs font-medium text-slate-500">Subject</label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)}
               className="mt-1 h-9 text-sm" data-testid="ops-email-subject" />

        <label className="mt-3 block text-xs font-medium text-slate-500">Message</label>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10}
                  className="mt-1 text-sm" data-testid="ops-email-body" />

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="ops-email-cancel">Cancel</Button>
          <Button variant="data" size="sm" onClick={send} disabled={busy} data-testid="ops-email-send">
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              : <Send className="mr-1.5 h-3.5 w-3.5" />}
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}
